import {
  Button,
  DashboardCard,
  DashboardGrid,
  DashboardLayout,
  cn,
} from '@zafroi/ui-kit';
import {
  ArrowRight,
  Bot,
  CheckCircle2,
  FileEdit,
  Loader,
  Mail,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useMemo } from 'react';
import {
  useBotKnowledge,
  useConversations,
  useSupportRequests,
} from '@/hooks/useEngageQueries';
import type { LucideIcon } from 'lucide-react';
import type { Tone } from '@zafroi/ui-kit';

/**
 * The Dashboard answers two questions for the tenant admin: "what needs
 * attention right now?" and "is the bot earning its keep?". Top: a welcome
 * banner that calls out pending work. Below: a four-card action queue
 * (pending / in-progress / resolved today / drafts) wired to click-through
 * routes. Below that: a Bot utilization card framing the self-improving-KB
 * loops. Both blocks click through to the actual workspace — the Dashboard
 * itself never owns a list view.
 *
 * Audience is the tenant admin; a separate agent-focused dashboard layout
 * (shift framing, assignee filters, SLA timers) will land when the
 * per-agent identity / routing model ships.
 */
export function DashboardPage() {
  const navigate = useNavigate();
  const supportQuery = useSupportRequests();
  const knowledgeQuery = useBotKnowledge();
  const conversationsQuery = useConversations();

  const supportRequests = supportQuery.data ?? [];
  const knowledge = knowledgeQuery.data ?? [];
  const conversations = conversationsQuery.data ?? [];

  const counts = useMemo(() => {
    const pending = supportRequests.filter((r) => r.status === 'pending').length;
    const inProgress = supportRequests.filter(
      (r) => r.status === 'in_progress',
    ).length;
    const today = new Date().toDateString();
    const resolvedToday = supportRequests.filter((r) => {
      if (r.status !== 'resolved' || !r.resolvedAt) return false;
      try {
        return new Date(r.resolvedAt).toDateString() === today;
      } catch {
        return false;
      }
    }).length;
    const drafts = knowledge.filter((k) => k.status === 'draft').length;
    return { pending, inProgress, resolvedToday, drafts };
  }, [supportRequests, knowledge]);

  // Bot utilization — three at-a-glance numbers that frame the bot's health:
  // how much it's actually doing, what it knows, and what's queued for review.
  // Together they make the Loop A → Loop B story visible from the home screen.
  const botStats = useMemo(() => {
    const cutoff = Date.now() - 7 * 24 * 60 * 60 * 1000;
    const botConversationsThisWeek = conversations.filter((c) => {
      if (c.channel !== 'portal') return false;
      const ts = Date.parse(c.lastMessageAt);
      return Number.isFinite(ts) && ts >= cutoff;
    }).length;
    const approvedKnowledge = knowledge.filter(
      (k) => k.status === 'approved',
    ).length;
    const draftsPending = knowledge.filter((k) => k.status === 'draft').length;
    return { botConversationsThisWeek, approvedKnowledge, draftsPending };
  }, [conversations, knowledge]);

  return (
    <DashboardLayout
      hero={
        <div className="flex flex-col gap-3">
          <WelcomeBanner
            pending={counts.pending}
            inProgress={counts.inProgress}
            onPendingClick={() => navigate('/support')}
          />
        </div>
      }
    >
      <DashboardGrid columns={4}>
        <ActionCard
          label="Pending requests"
          subtext="Customers waiting for a first reply"
          value={counts.pending}
          icon={Mail}
          tone={counts.pending > 0 ? 'warning' : 'primary'}
          ctaLabel="Open queue"
          onClick={() => navigate('/support')}
          loading={supportQuery.isLoading}
        />
        <ActionCard
          label="In progress"
          subtext="Replied — awaiting customer"
          value={counts.inProgress}
          icon={Loader}
          tone="accent"
          ctaLabel="Status check"
          onClick={() => navigate('/support')}
          loading={supportQuery.isLoading}
        />
        <ActionCard
          label="Resolved today"
          subtext="Requests closed in the last 24h"
          value={counts.resolvedToday}
          icon={CheckCircle2}
          tone="success"
          loading={supportQuery.isLoading}
        />
        <ActionCard
          label="Drafts to approve"
          subtext="Bot knowledge waiting for review"
          value={counts.drafts}
          icon={FileEdit}
          tone={counts.drafts > 0 ? 'warning' : 'primary'}
          ctaLabel="Review"
          onClick={() => navigate('/chatbot#knowledge')}
          loading={knowledgeQuery.isLoading}
        />
      </DashboardGrid>

      <DashboardCard
        title="Bot utilization"
        description="How much your bot is doing today, and what's queued to expand its reach."
        icon={Bot}
      >
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <BotStatTile
            label="Bot conversations · this week"
            value={botStats.botConversationsThisWeek}
            subtext="Customer questions the bot handled"
            tone="primary"
            loading={conversationsQuery.isLoading}
          />
          <BotStatTile
            label="Approved knowledge"
            value={botStats.approvedKnowledge}
            subtext="Topics the bot can answer"
            tone="success"
            loading={knowledgeQuery.isLoading}
          />
          <BotStatTile
            label="Drafts pending review"
            value={botStats.draftsPending}
            subtext="Approve to expand bot's reach"
            tone={botStats.draftsPending > 0 ? 'warning' : 'primary'}
            loading={knowledgeQuery.isLoading}
          />
        </div>
        <p className="mt-4 text-xs leading-relaxed text-muted-foreground">
          Every bot conversation feeds the self-improving knowledge base —
          gaps the bot couldn't fill become candidate articles (Loop A), and
          resolved cases auto-draft articles for next time (Loop B).{' '}
          <button
            type="button"
            onClick={() => navigate('/chatbot#gaps')}
            className="font-medium text-primary underline-offset-2 hover:underline focus:outline-none focus-visible:underline"
          >
            Open Chatbot Insights →
          </button>
        </p>
      </DashboardCard>

    </DashboardLayout>
  );
}

interface WelcomeBannerProps {
  pending: number;
  inProgress: number;
  onPendingClick: () => void;
}

function WelcomeBanner({
  pending,
  inProgress,
  onPendingClick,
}: WelcomeBannerProps) {
  if (pending === 0) {
    return (
      <div className="flex items-center gap-2 rounded-md border border-border bg-muted/40 px-3 py-2 text-sm text-muted-foreground">
        <CheckCircle2 className="h-4 w-4 text-success" />
        <span>All caught up · no pending requests</span>
      </div>
    );
  }
  return (
    <div className="flex flex-wrap items-center gap-2 rounded-md border border-warning/40 bg-warning/10 px-3 py-2 text-sm text-foreground">
      <Mail className="h-4 w-4 text-warning" />
      <span>
        Welcome back ·{' '}
        <button
          type="button"
          onClick={onPendingClick}
          className="font-semibold text-warning underline-offset-2 hover:underline focus:outline-none focus-visible:underline"
        >
          {pending} {pending === 1 ? 'request' : 'requests'} pending
        </button>
        , {inProgress} in progress
      </span>
    </div>
  );
}

interface ActionCardProps {
  label: string;
  subtext: string;
  value: number;
  icon: LucideIcon;
  tone: Tone;
  ctaLabel?: string;
  onClick?: () => void;
  loading?: boolean;
}

const toneToValueClass: Record<Tone, string> = {
  neutral: 'text-foreground',
  primary: 'text-primary',
  accent: 'text-accent',
  success: 'text-success',
  warning: 'text-warning',
  destructive: 'text-destructive',
  info: 'text-info',
};

function ActionCard({
  label,
  subtext,
  value,
  icon: Icon,
  tone,
  ctaLabel,
  onClick,
  loading,
}: ActionCardProps) {
  return (
    <DashboardCard
      className="cursor-default"
      footer={
        ctaLabel && onClick ? (
          <Button
            variant="ghost"
            size="sm"
            className="-ml-2 h-7 px-2 text-xs"
            onClick={onClick}
          >
            {ctaLabel}
            <ArrowRight className="h-3 w-3" />
          </Button>
        ) : undefined
      }
    >
      <div className="flex items-start justify-between gap-3">
        <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
          {label}
        </span>
        <span className="inline-flex h-7 w-7 items-center justify-center rounded-md bg-muted text-muted-foreground">
          <Icon className="h-4 w-4" />
        </span>
      </div>
      {loading ? (
        <div className="mt-3 h-8 w-16 animate-pulse rounded-md bg-muted/70" />
      ) : (
        <div
          className={cn(
            'mt-3 tnum text-3xl font-semibold tracking-tight',
            toneToValueClass[tone],
          )}
        >
          {value}
        </div>
      )}
      <p className="mt-1 text-xs text-muted-foreground">{subtext}</p>
    </DashboardCard>
  );
}

/**
 * One stat tile inside the Bot utilization card. Smaller and tonal — these
 * are dashboard-strip numbers, not action cards (no per-tile CTA; the card
 * footer link covers the click-through).
 */
function BotStatTile({
  label,
  value,
  subtext,
  tone,
  loading,
}: {
  label: string;
  value: number;
  subtext: string;
  tone: Tone;
  loading?: boolean;
}) {
  return (
    <div className="rounded-lg border border-border bg-muted/30 px-3 py-3">
      <div className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
        {label}
      </div>
      {loading ? (
        <div className="mt-2 h-7 w-12 animate-pulse rounded-md bg-muted/70" />
      ) : (
        <div
          className={cn(
            'mt-1 tnum text-2xl font-semibold tracking-tight',
            toneToValueClass[tone],
          )}
        >
          {value}
        </div>
      )}
      <p className="mt-1 text-xs text-muted-foreground">{subtext}</p>
    </div>
  );
}

